// Multi-currency support: IP-based detection + FX conversion.
// Charged in USD by Stripe; this only changes display.

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export type SupportedCurrency =
  | "USD" | "EUR" | "GBP" | "CAD" | "AUD" | "INR" | "AED";

export const CURRENCIES: Record<
  SupportedCurrency,
  { label: string; symbol: string; flag: string }
> = {
  USD: { label: "US Dollar", symbol: "$", flag: "🇺🇸" },
  EUR: { label: "Euro", symbol: "€", flag: "🇪🇺" },
  GBP: { label: "British Pound", symbol: "£", flag: "🇬🇧" },
  CAD: { label: "Canadian Dollar", symbol: "CA$", flag: "🇨🇦" },
  AUD: { label: "Australian Dollar", symbol: "A$", flag: "🇦🇺" },
  INR: { label: "Indian Rupee", symbol: "₹", flag: "🇮🇳" },
  AED: { label: "UAE Dirham", symbol: "AED ", flag: "🇦🇪" },
};

// Country → preferred currency among our supported set.
const COUNTRY_TO_CURRENCY: Record<string, SupportedCurrency> = {
  US: "USD", GB: "GBP", CA: "CAD", AU: "AUD", IN: "INR",
  AE: "AED",
  // Eurozone
  DE: "EUR", FR: "EUR", IT: "EUR", ES: "EUR", NL: "EUR", BE: "EUR",
  AT: "EUR", PT: "EUR", IE: "EUR", FI: "EUR", GR: "EUR", LU: "EUR",
  SK: "EUR", SI: "EUR", LT: "EUR", LV: "EUR", EE: "EUR", CY: "EUR",
  MT: "EUR", HR: "EUR",
};

interface CurrencyState {
  currency: SupportedCurrency;
  rates: Partial<Record<SupportedCurrency, number>>; // base USD
  ratesUpdatedAt: number;
  detectedAt: number;
  manualOverride: boolean;
  setCurrency: (c: SupportedCurrency, manual?: boolean) => void;
  initialize: () => Promise<void>;
}

const RATES_TTL = 12 * 60 * 60 * 1000; // 12h
const DETECT_TTL = 24 * 60 * 60 * 1000; // 24h

async function fetchRates(): Promise<Partial<Record<SupportedCurrency, number>>> {
  try {
    const r = await fetch("https://open.er-api.com/v6/latest/USD");
    const j = await r.json();
    if (j.result !== "success" || !j.rates) throw new Error("bad rates");
    const out: Partial<Record<SupportedCurrency, number>> = { USD: 1 };
    (Object.keys(CURRENCIES) as SupportedCurrency[]).forEach((c) => {
      if (typeof j.rates[c] === "number") out[c] = j.rates[c];
    });
    return out;
  } catch {
    return { USD: 1 };
  }
}

async function detectCountry(): Promise<string | null> {
  // Use Cloudflare's trace endpoint — reliable, no rate limits, no 403s
  try {
    const r = await fetch("https://www.cloudflare.com/cdn-cgi/trace", {
      signal: AbortSignal.timeout(2500),
    });
    if (!r.ok) return null;
    const text = await r.text();
    const match = text.match(/^loc=([A-Z]{2})$/m);
    if (match) return match[1];
  } catch { /* ignore */ }
  return null;
}

export const useCurrencyStore = create<CurrencyState>()(
  persist(
    (set, get) => ({
      currency: "USD",
      rates: { USD: 1 },
      ratesUpdatedAt: 0,
      detectedAt: 0,
      manualOverride: false,

      setCurrency: (c, manual = true) => {
        set({ currency: c, manualOverride: manual });
      },

      initialize: async () => {
        const { ratesUpdatedAt, detectedAt, manualOverride, currency } = get();
        const now = Date.now();

        // Refresh rates
        if (now - ratesUpdatedAt > RATES_TTL) {
          const rates = await fetchRates();
          set({ rates, ratesUpdatedAt: now });
        }

        // Always force USD on Lovable preview/sandbox domains
        const host = typeof window !== "undefined" ? window.location.hostname : "";
        const isLovablePreview = host.endsWith("lovable.app") || host.endsWith("lovableproject.com") || host.endsWith("lovable.dev");
        if (isLovablePreview) {
          if (get().currency !== "USD") set({ currency: "USD" });
          set({ detectedAt: now, manualOverride: false });
          return;
        }

        // Detect currency from IP unless user has manually picked one
        if (!isLovablePreview && !manualOverride && now - detectedAt > DETECT_TTL) {
          const cc = await detectCountry();
          if (cc) {
            const next = COUNTRY_TO_CURRENCY[cc];
            if (next && next !== currency) {
              set({ currency: next, detectedAt: now });
            } else {
              set({ detectedAt: now });
            }
          }
        }
      },
    }),
    {
      name: "pk-currency",
      storage: createJSONStorage(() => localStorage),
    }
  )
);

export function convertFromUsd(amountUsd: number, target: SupportedCurrency, rates: Partial<Record<SupportedCurrency, number>>): number {
  const rate = rates[target] ?? 1;
  return amountUsd * rate;
}

export function formatMoney(amount: number, currency: SupportedCurrency): string {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: ["INR", "JPY"].includes(currency) ? 0 : 2,
    }).format(amount);
  } catch {
    const sym = CURRENCIES[currency]?.symbol ?? "";
    return `${sym}${amount.toFixed(2)}`;
  }
}

/** Convenience hook: returns formatter that converts a USD amount to current display currency. */
export function useCurrency() {
  const currency = useCurrencyStore((s) => s.currency);
  const rates = useCurrencyStore((s) => s.rates);

  const format = (usd: number) => {
    const converted = convertFromUsd(usd, currency, rates);
    return formatMoney(converted, currency);
  };

  return { currency, rates, format, isUsd: currency === "USD" };
}
